import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DSText } from '@engenharia/ds-react';
import {
  Box,
  Container,
  ContainerRadio,
  Content,
  Counter,
  CounterLabel,
  CounterOption,
  CounterRadio,
  FormInput,
  Heading,
  HeadingSubtitle,
} from './styles';
import { CoreApiContext } from '../../../contexts/coreApiContext';
import { getLanguage, getAccountType } from '../../../services/auth';
import { Keys } from '../../../constants';
import { logError } from '../../../utils/logError';
import { getFromLocalStorage, removeFromLocalStorage } from '../../../utils/localStorage';
import { sendEventLog } from '../../../services/eventLog';
import { setDataLayer } from '../../../services/gtm';
import { transcodeLanguage } from '../../../utils/language';
import api from '../../../services/api';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import iconCheckList from '../../../assets/icons/check-list.svg';
import Loading from '../../../components/atoms/Loading';
import Notification from '../../../components/Notification';

export default function Profiles() {
  const { currentAccount } = useContext(CoreApiContext);
  const [profiles, setProfiles] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [canShowProfilesMenu, setCanShowProfilesMenu] = useState(false);
  const [notification, setNotification] = useState({});
  const [optionProfilesQuantity] = useState([
    { key: 'one_profile', quantityProfile: 1 },
    { key: 'five_profiles', quantityProfile: 5 },
    { key: 'nine_profiles', quantityProfile: 9 },
    { key: 'ten_more_profiles', quantityProfile: 10 },
  ]);

  const { t } = useTranslation();
  const hasProfiles = !!profiles;
  const couponCode = getFromLocalStorage(Keys.COUPON_CODE) || '';
  const language = transcodeLanguage(getLanguage());
  const accountType = getAccountType() || 'email';
  const registerType = accountType === 'email' ? 'metodo_formulario' : 'metodo_facebook';
  const userPlanType = currentAccount?.quotas?.available ? 'subscriber' : 'trial';

  const sendEventGtm = ({ element, elementText, numberOfSelectedProfiles, userAction }) => {
    setDataLayer({
      event: 'siteFireEvt.accountsCreationFlow.conversionEvent',
      eventProperty: {
        numberOfSelectedProfiles,
        element,
        elementText,
        userAction,
        primarySource: 'app',
        codeSource: 'front-->gtm-->endpoint',
        analiticalContext: 'comportamental',
        primaryContext: 'accounts',
        elementOriginalText: 'nao_aplicavel',
        appContext: 'cadastrar',
        appAction: 'finalizar_cadastro_step_perfis',
        socialNetwork: 'nao_aplicavel',
        postChannel: 'nao_aplicavel',
        numberOfDatesToPost: 0,
        totalPostScheduled: 0,
        appDetails: `${registerType} cadastro_realizado_novo_usuario`,
        siteLanguage: language,
        registerMethod: registerType,
        siteEventDetails: `cadastrar.finalizar_cadastro_step_perfis.${registerType}.cadastro_realizado_novo_usuario`,
      },
      userProperty: {
        userCostumerStatus: userPlanType,
      },
    });
  };

  function accessRightStepProfiles(account) {
    const accessRights = account.access_rights;
    const access = 'sign_up_step_profiles';

    return access in accessRights ? accessRights[access] : true;
  }

  const handleUpdateProfiles = async () => {
    setIsLoading(true);

    try {
      await api.patch(`/accounts/${currentAccount.id}`, {
        accepted_onboarding_at: new Date(),
        start_profiles_count: profiles,
        language,
        coupon_code: couponCode,
      });

      removeFromLocalStorage(Keys.ACCOUNT_CREATOR);
      removeFromLocalStorage(Keys.COUPON_CODE);

      sendEventGtm({
        numberOfSelectedProfiles: profiles,
        element: 'botao',
        elementText: 'Iniciar período de teste',
        userAction: 'clicou',
      });

      try {
        await sendEventLog('signup_click_start-trial-period-button', {
          origin: currentAccount.origin_key,
        });
      } catch {
        logError(t('errors.send-event-log'));
      }

      window.location.assign(process.env.REACT_APP_FULL_URL);
    } catch (error) {
      setIsLoading(false);
      setNotification({
        key: new Date().getTime(),
        message: error?.response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const handleRedirectAccountPartner = async () => {
    await api.patch(`/accounts/${currentAccount.id}`, {
      accepted_onboarding_at: new Date(),
      start_profiles_count: 0,
      language,
      coupon_code: '',
    });

    window.location.assign(process.env.REACT_APP_FULL_URL);
  };

  useEffect(() => {
    if (currentAccount?.id) {
      sendEventGtm({
        numberOfSelectedProfiles: 0,
        userAction: 'visitou',
        element: 'pagina',
        elementText: 'nao_aplicavel',
      });

      if (!accessRightStepProfiles(currentAccount)) {
        handleRedirectAccountPartner();
      } else {
        setCanShowProfilesMenu(true);
      }
    }
  }, [currentAccount]);

  return (
    <Container>
      {(!canShowProfilesMenu || isLoading) && <Loading opacity />}
      {canShowProfilesMenu && (
        <>
          <Header showLogo />
          <Content>
            <Box>
              <img src={iconCheckList} alt="" />
              <Heading component="h2" variant="h4">
                {t('settings.steps.profiles.heading')}
              </Heading>
              <HeadingSubtitle component="h3" variant="h5">
                {t('settings.steps.profiles.headingSubtitle')}
              </HeadingSubtitle>
              <FormInput>
                {optionProfilesQuantity.map((option) => (
                  <Counter
                    key={option.key}
                    onClick={() => {
                      setProfiles(option.quantityProfile);
                    }}
                    isChecked={option.quantityProfile === profiles}
                  >
                    <ContainerRadio>
                      <CounterRadio isChecked={option.quantityProfile === profiles} />
                      <CounterOption>
                        {t(`settings.steps.profiles.options.${option.key}`)}
                      </CounterOption>
                    </ContainerRadio>
                    <CounterLabel>
                      <DSText variant="body2">
                        {option.quantityProfile === 1
                          ? t('settings.steps.profiles.single.label')
                          : t('settings.steps.profiles.several.label')}
                      </DSText>
                    </CounterLabel>
                  </Counter>
                ))}
              </FormInput>
            </Box>
          </Content>
          <Footer
            activeStep={1}
            listStep={[0, 1]}
            onGoNext={handleUpdateProfiles}
            disabled={!hasProfiles}
          />
          {!!notification?.key && (
            <Notification index={notification.key} message={notification.message} />
          )}
        </>
      )}
    </Container>
  );
}
